import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronDown, ChevronUp, Loader2, Shield } from 'lucide-react';
import { apiFetch } from '../../shared/apiClient';
import { resolveBillingPlansFromApi } from '../../shared/billing/defaultPublicPlans';
import { brand } from '../../shared/config/brand';
import { useAuth } from '../../contexts/AuthContext';
import { PlanCard, type BillingCycle, type PlanCardPlan } from '../../components/PlanCard/PlanCard';

const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: 'What is the Design Partner Pilot?',
    a: 'A founder-led path on your real Jira workspace: we scope success criteria, walk capacity and release risk together, and tune onboarding—not a generic sandbox trial.',
  },
  {
    q: 'What happens after the trial?',
    a: 'After 14 days, choose a paid plan to keep full access. Your data stays with your organization.',
  },
  {
    q: 'Can I change plans later?',
    a: 'Yes. Upgrade or downgrade from Settings → Billing or the Stripe customer portal.',
  },
  {
    q: 'What is the refund policy?',
    a: 'We offer a 30-day money-back guarantee on new subscriptions. Cancel anytime from the billing portal.',
  },
  {
    q: 'What counts as a product?',
    a: 'A product maps to a Jira project (or equivalent) inside your organization — each product has its own settings and analytics.',
  },
  {
    q: 'Do you offer annual billing?',
    a: 'Yes. Annual billing includes about 17% savings compared to paying monthly.',
  },
];

export const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, currentOrg } = useAuth();
  const [plans, setPlans] = useState<PlanCardPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [checkoutSlug, setCheckoutSlug] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/billing/plans');
        if (!res.ok) throw new Error('load');
        const data = await res.json();
        if (!cancelled) setPlans(resolveBillingPlansFromApi(data.plans));
      } catch {
        if (!cancelled) setPlans(resolveBillingPlansFromApi(null));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCta = useCallback(
    async (slug: string) => {
      if (slug === 'growth') {
        window.location.href = `mailto:${brand.salesEmail}?subject=${encodeURIComponent('Apply for Design Partner Pilot')}`;
        return;
      }
      if (slug === 'enterprise') {
        window.location.href = `mailto:${brand.salesEmail}?subject=${encodeURIComponent('Enterprise inquiry')}`;
        return;
      }
      if (!user) {
        navigate('/auth');
        return;
      }
      if (!currentOrg?.id) {
        navigate('/onboarding');
        return;
      }
      setCheckoutSlug(slug);
      try {
        const res = await apiFetch('/api/billing/checkout', {
          method: 'POST',
          headers: {
            'X-Organization-Id': currentOrg.id,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ planSlug: slug, billingCycle }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setCheckoutSlug(null);
          return;
        }
        const url = (data as { url?: string }).url;
        if (url) window.location.href = url;
      } catch {
        setCheckoutSlug(null);
      }
    },
    [user, currentOrg?.id, billingCycle, navigate]
  );

  const pricingRows = useMemo((): PlanCardPlan[] => {
    const growth = plans.find((p) => p.slug === 'growth');
    const scale = plans.find((p) => p.slug === 'scale');
    const enterprise = plans.find((p) => p.slug === 'enterprise');
    const rows: PlanCardPlan[] = [];
    if (growth) rows.push(growth);
    if (scale) rows.push(scale);
    if (enterprise) rows.push(enterprise);
    return rows;
  }, [plans]);

  return (
    <div className="pricing-page">
      <section className="pricing-hero animate-fade-in">
        <h1 className="pricing-hero__title">{brand.pricingHeroTitle}</h1>
        <p className="pricing-hero__sub">{brand.pricingHeroSub}</p>
        <div className="pricing-toggle">
          <button
            type="button"
            className={billingCycle === 'monthly' ? 'active' : ''}
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly
          </button>
          <button
            type="button"
            className={billingCycle === 'annual' ? 'active' : ''}
            onClick={() => setBillingCycle('annual')}
          >
            Annual
            <span className="pricing-toggle__save">Save 17%</span>
          </button>
        </div>
      </section>

      {loading ? (
        <div className="pricing-loading">
          <Loader2 className="pricing-loading__spin" />
        </div>
      ) : (
        <section className="pricing-grid-section">
          <div className="pricing-grid">
            {pricingRows.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                billingCycle={billingCycle}
                featured={p.slug === 'scale'}
                badge={p.slug === 'scale' ? 'Most popular' : undefined}
                isEnterprise={p.slug === 'enterprise'}
                showPlanLimits={p.slug !== 'organization-contact'}
                ctaLabel={
                  p.slug === 'growth'
                    ? brand.pilotCta
                    : p.slug === 'enterprise'
                      ? 'Contact Sales'
                      : user
                        ? checkoutSlug === p.slug
                          ? 'Redirecting…'
                          : 'Start Guided Trial'
                        : 'Start Guided Trial'
                }
                disabled={p.slug === 'scale' && checkoutSlug === 'scale'}
                onCta={() => void handleCta(p.slug)}
              />
            ))}
          </div>
        </section>
      )}

      <section className="pricing-guarantee animate-slide-up">
        <Shield className="pricing-guarantee__icon" size={28} aria-hidden />
        <div>
          <strong>30-day money-back guarantee</strong>
          <p>
            Cancel anytime from your billing portal. We bias toward teams that actually ship—we
            would rather earn your renewal than trap you.
          </p>
        </div>
      </section>

      <section className="pricing-faq">
        <h2 className="pricing-faq__title">FAQ</h2>
        <div className="pricing-faq__list">
          {FAQ_ITEMS.map((item, i) => (
            <div key={item.q} className="pricing-faq__item">
              <button
                type="button"
                className="pricing-faq__question"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                aria-expanded={openFaq === i}
              >
                <span>{item.q}</span>
                {openFaq === i ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {openFaq === i ? (
                <div className="pricing-faq__answer">
                  <CheckCircle className="pricing-faq__check" size={18} aria-hidden />
                  {item.a}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
